import DataTable from "../../components/common/DataTable";
import BunnyVideoSelector from "../../components/common/BunnyVideoSelector";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Paper, Menu, Modal, Switch, TextInput, Textarea, NumberInput, Group, Stack } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { Check, MoreHorizontal, Pencil, Plus, Trash, Video, X } from "lucide-react";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import { Link, useParams } from "react-router-dom";
import Head from "../../components/common/Head";
import {
  addVerticalEpisode,
  deleteVerticalEpisode,
  getVerticalEpisodesBySeriesId,
  getVerticalSeriesById,
  updateVerticalEpisode,
} from "../../utils/api";
import { videoAPI } from "../../services/bunnyApi";
import useStore from "../../utils/store";

const defaultEpisodeForm = { name: "", description: "", position: 1, duration: 0, url: "", urlType: "", releaseDate: null, subtitles: "", isFree: true, isEnable: false };

const VerticalEpisodes = () => {
  const [episodeForm, setEpisodeForm] = useState(defaultEpisodeForm);
  const user = useStore((state) => state.user);
  const params = useParams();
  const seriesId = params.id;
  
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [bunnyVideoOpen, setBunnyVideoOpen] = useState(false);

  // Fetch series details
  const { data: seriesData } = useQuery({
    queryKey: ["vertical-series-detail", seriesId],
    queryFn: () => getVerticalSeriesById(seriesId),
    enabled: !!seriesId,
  });

  // Fetch episodes
  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const [success, data] = await getVerticalEpisodesBySeriesId(seriesId);
      if (success) {
        setEpisodes(data || []);
      }
    } catch (error) {
      notifications.show({ message: "Failed to fetch episodes", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (seriesId) {
      fetchEpisodes();
    }
  }, [seriesId]);

  // Mutations
  const addEpisodeMutation = useMutation({
    mutationFn: (data) => addVerticalEpisode(data),
    onSuccess: ([success]) => {
      if (success) {
        notifications.show({ message: "Episode added successfully", color: "green" });
        setModalVisible(false);
        setEpisodeForm(defaultEpisodeForm);
        fetchEpisodes();
      } else {
        notifications.show({ message: "Failed to add episode", color: "red" });
      }
    },
    onError: () => notifications.show({ message: "Failed to add episode", color: "red" }),
  });

  const updateEpisodeMutation = useMutation({
    mutationFn: ({ id, data }) => updateVerticalEpisode(id, data),
    onSuccess: ([success]) => {
      if (success) {
        notifications.show({ message: "Episode updated successfully", color: "green" });
        setModalVisible(false);
        setEditingEpisode(null);
        setEpisodeForm(defaultEpisodeForm);
        fetchEpisodes();
      } else {
        notifications.show({ message: "Failed to update episode", color: "red" });
      }
    },
    onError: () => notifications.show({ message: "Failed to update episode", color: "red" }),
  });

  const deleteEpisodeMutation = useMutation({
    mutationFn: (id) => deleteVerticalEpisode(id),
    onSuccess: ([success]) => {
      if (success) {
        notifications.show({ message: "Episode deleted successfully", color: "green" });
        fetchEpisodes();
      } else {
        notifications.show({ message: "Failed to delete episode", color: "red" });
      }
    },
    onError: () => {
      notifications.show({ message: "Failed to delete episode", color: "red" });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isEnable }) => updateVerticalEpisode(id, { isEnable }),
    onSuccess: ([success]) => {
      if (success) {
        notifications.show({ message: "Episode status updated", color: "green" });
        fetchEpisodes();
      } else {
        notifications.show({ message: "Failed to update episode status", color: "red" });
      }
    },
    onError: () => {
      notifications.show({ message: "Failed to update episode status", color: "red" });
    },
  });

  const handleAddEpisode = () => {
    setEditingEpisode(null);
    setEpisodeForm({ ...defaultEpisodeForm, position: episodes.length + 1 });
    setModalVisible(true);
  };

  const handleEditEpisode = (episode) => {
    setEditingEpisode(episode);
    setEpisodeForm({
      name: episode.name || "",
      description: episode.description || "",
      position: episode.position || 1,
      duration: episode.duration || 0,
      url: episode.url || "",
      urlType: episode.urlType || "",
      isEnable: episode.isEnable || false,
      isFree: episode.isFree !== false,
      releaseDate: episode.releaseDate ? new Date(episode.releaseDate) : null,
      subtitles: episode.subtitles || "",
    });
    setModalVisible(true);
  };

  const handleFormSubmit = () => {
    const data = {
      name: episodeForm.name,
      series: seriesId,
      position: episodeForm.position,
      description: episodeForm.description,
      duration: episodeForm.duration || 0,
      url: episodeForm.url,
      urlType: episodeForm.urlType,
      isEnable: episodeForm.isEnable || false,
      isFree: episodeForm.isFree !== false,
      view: 0,
      releaseDate: episodeForm.releaseDate ? dayjs(episodeForm.releaseDate).toISOString() : null,
      subtitles: episodeForm.subtitles,
    };
    if (editingEpisode) updateEpisodeMutation.mutate({ id: editingEpisode._id, data });
    else addEpisodeMutation.mutate(data);
  };

  const setField = (field, value) => setEpisodeForm((prev) => ({ ...prev, [field]: value }));

  const openVideoModal = (url) => {
    setCurrentVideoUrl(url);
    setVideoModalVisible(true);
  };

  const handleSelectBunnyEpisodeVideo = (video) => {
    setEpisodeForm((prev) => ({ ...prev, url: videoAPI.getPlaylistUrl(video.guid), urlType: "bunny" }));
    notifications.show({
      message: `"${video.title}" selected`,
      color: "green",
    });
  };

  const columns = [
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Menu
          shadow="md"
          width={200}
          trigger="click"
        >
          <Menu.Target>
            <Button><MoreHorizontal size={16} /></Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<Pencil size={16} />}
              onClick={() => handleEditEpisode(record)}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<Trash size={16} />}
              onClick={() => modals.openConfirmModal({
                title: "Are you sure you want to delete this episode?",
                labels: { confirm: "Yes", cancel: "No" },
                onConfirm: () => deleteEpisodeMutation.mutate(record._id),
              })}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ),
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      sorter: (a, b) => a.position - b.position,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "isEnable",
      key: "isEnable",
      render: (isEnable) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            border: "1px solid ",
            borderColor: isEnable ? "#00ff0050" : "#ff000050",
            backgroundColor: isEnable ? "#00ff0050" : "#ff000050",
          }}
        >
          {isEnable ? <Check size={16} /> : <X size={16} />}
        </div>
      ),
    },
    {
      title: "Status Action",
      key: "statusAction",
      render: (_, record) => (
        <Button color={record.isEnable ? "red" : "green"}
          variant="filled"
          onClick={() =>
            toggleStatusMutation.mutate({
              id: record._id,
              isEnable: !record.isEnable,
            })
          }
          loading={toggleStatusMutation.isPending}
        >
          {!record.isEnable ? "Enable" : "Disable"}
        </Button>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <div style={{ width: 200, overflow: "hidden" }}>
          {text?.slice(0, 50)}...
        </div>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (duration) => (duration ? `${duration} min` : "N/A"),
    },
    {
      title: "Video",
      dataIndex: "url",
      key: "url",
      render: (url) =>
        url ? (
          <Button variant="subtle" onClick={() => openVideoModal(url)}>
            Play Video
          </Button>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Is Free",
      dataIndex: "isFree",
      key: "isFree",
      render: (isFree) => (isFree ? "Yes" : "No"),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      
      <Head
        title={`Episodes - ${seriesData?.name || "Vertical Series"}`}
        customButton={
          <div style={{ display: "flex", gap: "8px" }}>
            <Link to="/vertical-series">
              <Button>Back to Vertical Series</Button>
            </Link>
            <Button
              onClick={handleAddEpisode}
              icon={<Plus size={16} />}
            >
              Add Episode
            </Button>
          </div>
        }
      />

      <Paper withBorder p="md">
        <DataTable
          rowKey="_id"
          columns={columns}
          dataSource={episodes}
          loading={loading}
          scroll={{ x: 1000 }}
        />
      </Paper>

      {/* Add/Edit Episode Modal */}
      <Modal
        title={editingEpisode ? "Edit Episode" : "Add Episode"}
        opened={modalVisible}
        onClose={() => { setModalVisible(false); setEditingEpisode(null); setEpisodeForm(defaultEpisodeForm); }}
        size="lg"
      >
        <Stack gap="md">
          <TextInput label="Name" required placeholder="Episode name" value={episodeForm.name} onChange={(e) => setField("name", e.target.value)} />
          <Textarea label="Description" rows={3} placeholder="Episode description" value={episodeForm.description} onChange={(e) => setField("description", e.target.value)} />
          <NumberInput label="Position" min={1} required value={episodeForm.position} onChange={(v) => setField("position", v)} />
          <NumberInput label="Duration (minutes)" min={0} value={episodeForm.duration} onChange={(v) => setField("duration", v)} />
          <div>
            <div style={{ fontSize: 14, marginBottom: 6, fontWeight: 500 }}>Video URL</div>
            <Group gap="xs" align="flex-start">
              <TextInput
                placeholder="Select from Bunny or paste HLS / video URL"
                style={{ flex: 1 }}
                value={episodeForm.url}
                onChange={(e) => setField("url", e.target.value)}
              />
              <Button variant="default" leftSection={<Video size={16} />} onClick={() => setBunnyVideoOpen(true)}>Bunny</Button>
            </Group>
          </div>
          <DatePickerInput label="Release Date" value={episodeForm.releaseDate} onChange={(v) => setField("releaseDate", v)} />
          <TextInput label="Subtitles URL" placeholder="Subtitles URL" value={episodeForm.subtitles} onChange={(e) => setField("subtitles", e.target.value)} />
          <Switch label={episodeForm.isFree ? "Free" : "Paid"} checked={episodeForm.isFree} onChange={(e) => setField("isFree", e.currentTarget.checked)} />
          <Switch label={episodeForm.isEnable ? "Enabled" : "Disabled"} checked={episodeForm.isEnable} onChange={(e) => setField("isEnable", e.currentTarget.checked)} />
          <Button fullWidth onClick={handleFormSubmit} loading={addEpisodeMutation.isPending || updateEpisodeMutation.isPending}>
            {editingEpisode ? "Update Episode" : "Add Episode"}
          </Button>
        </Stack>
      </Modal>

      {/* Video Modal */}
      <Modal
        opened={videoModalVisible}
        onClose={() => {
          setVideoModalVisible(false);
          setCurrentVideoUrl("");
        }}
        width={800}
        footer={null}
        destroyOnClose
      >
        <div style={{ width: "100%", height: "450px" }}>
          <ReactPlayer
            url={currentVideoUrl}
            width="100%"
            height="100%"
            controls
            playing
          />
        </div>
      </Modal>

      <BunnyVideoSelector
        open={bunnyVideoOpen}
        onCancel={() => setBunnyVideoOpen(false)}
        onSelect={handleSelectBunnyEpisodeVideo}
        title="Select episode video (Bunny)"
        verticalOnly
      />
    </div>
  );
};

export default VerticalEpisodes;
